import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RULES = [
  { key: "len", label: "≥ 14 characters", test: (v) => v.length >= 14 },
  { key: "upper", label: "1 uppercase", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "1 lowercase", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "1 number", test: (v) => /\d/.test(v) },
  { key: "special", label: "1 special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function FirstLoginModal() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!user && !!user.must_change_password;

  const failed = RULES.filter((r) => !r.test(pw));
  const canSubmit = failed.length === 0 && pw === pw2 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated. Welcome to Home360.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  }

  function signOutInstead() {
    logout();
    toast.info("Signed out. You can complete the password change on your next sign-in.");
    nav("/login");
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) signOutInstead(); }}>
      <DialogContent className="max-w-md" data-testid="first-login-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Set a new password</DialogTitle>
        </DialogHeader>
        <p className="subtle -mt-1">For security, you must replace the temporary password before you continue.</p>
        <div className="space-y-4 mt-4">
          <div>
            <Label>New password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password-input" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="confirm-password-input" />
          </div>
          <ul className="grid grid-cols-2 gap-1 text-xs">
            {RULES.map((r) => (
              <li key={r.key} className={r.test(pw) ? "text-success" : "text-muted-foreground"}>
                {r.test(pw) ? "✓" : "•"} {r.label}
              </li>
            ))}
            <li className={pw && pw === pw2 ? "text-success" : "text-muted-foreground"}>
              {pw && pw === pw2 ? "✓" : "•"} passwords match
            </li>
          </ul>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={signOutInstead}
            data-testid="first-login-signout-btn"
          >
            Sign out
          </Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="set-password-btn">
            {busy ? "Saving…" : "Set new password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
